import { LitElement, html, css } from 'lit';

import {
  CSS_COLOR_GREY_1,
  CSS_COLOR_HIGHLIGHT,
} from '../../../../neb-styles/neb-variables';

export const ELEMENTS = {
  verificationCodeInputs: { id: 'verification-code-inputs' },
};

class NebTwoFactorAuth extends LitElement {
  static get properties() {
    return {
      __verificationCode: { type: Array },
      __error: { type: Boolean },
      __codeResent: { type: Boolean },
      authenticator: { type: Boolean },
      strictValidation: { type: Boolean },
      autoSubmitOnComplete: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.__verificationCode = ['', '', '', '', '', ''];
    this.__error = false;
    this.__codeResent = false;
    this.authenticator = false;
    this.strictValidation = false;
    this.autoSubmitOnComplete = false;

    const originalOnChange = () => {};

    this.onChange = code => {
      originalOnChange(code);

      if (this.__error) {
        setTimeout(() => {
          const firstInput = this.shadowRoot?.querySelector('.code-input');

          if (firstInput) {
            firstInput.focus();
            firstInput.select();
          }
        }, 0);
      }
    };

    this.onResend = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      verificationCodeChange: (index, e) => {
        this.__codeResent = false;

        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        e.target.value = numericValue;

        this.__verificationCode = [
          ...this.__verificationCode.slice(0, index),
          numericValue,
          ...this.__verificationCode.slice(index + 1),
        ];

        const allInputsFilled = this.strictValidation
          ? this.__verificationCode.every(digit => digit && /^\d$/.test(digit))
          : this.__verificationCode.some(digit => digit);

        this.onChange(this.__verificationCode.join(''), allInputsFilled);

        if (index === 5 && numericValue) {
          setTimeout(() => {
            const firstInput = this.shadowRoot?.querySelector('.code-input');

            if (firstInput) {
              firstInput.focus();
              firstInput.select();
            }
          }, 0);
        } else if (numericValue && index < 5) {
          const nextInput =
            this.shadowRoot.querySelectorAll('.code-input')[index + 1];
          if (nextInput) nextInput.focus();
        }

        this.requestUpdate();
      },

      handleBackspace: index => {
        if (!this.__verificationCode[index] && index > 0) {
          const prevInput =
            this.shadowRoot.querySelectorAll('.code-input')[index - 1];

          if (prevInput) {
            prevInput.focus();
            this.__verificationCode = [
              ...this.__verificationCode.slice(0, index - 1),
              '',
              ...this.__verificationCode.slice(index),
            ];
          }
        } else {
          this.__verificationCode = [
            ...this.__verificationCode.slice(0, index),
            '',
            ...this.__verificationCode.slice(index + 1),
          ];
        }

        const allInputsFilled = this.strictValidation
          ? this.__verificationCode.every(digit => digit && /^\d$/.test(digit))
          : this.__verificationCode.some(digit => digit);

        this.onChange(this.__verificationCode.join(''), allInputsFilled);

        this.requestUpdate();
      },

      handleKeyDown: (index, e) => {
        switch (e.key) {
          case 'Tab':
            if (this.__handlers.handleTabKey(index, e));
            break;
          case 'Backspace':
            this.__handlers.handleBackspaceKey(index, e);
            break;
          case 'Enter':
            this.__handlers.handleEnter();
            break;
          default:
            break;
        }
      },

      handleTabKey: (index, e) => {
        if (e.shiftKey && index === 0) {
          e.preventDefault();
          return true;
        }

        if (index === 5 && !e.shiftKey) {
          this.__handlers.handleTabOnLastInput(e);
          return true;
        }
        return false;
      },

      isBackspace: e => e.key === 'Backspace',

      isTabOnLastInput: (index, e) =>
        e.key === 'Tab' && index === 5 && !e.shiftKey,

      isEnter: e => e.key === 'Enter',

      handleBackspaceKey: (index, e) => {
        e.preventDefault();
        this.__handlers.handleBackspace(index);
      },

      handleTabOnLastInput: e => {
        e.preventDefault();
        const event = new CustomEvent('tabbed-past-last-input', {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(event);
      },

      handleEnter: () => {
        const allInputsFilled = this.strictValidation
          ? this.__verificationCode.every(digit => digit && /^\d$/.test(digit))
          : this.__verificationCode.some(digit => digit);

        if (allInputsFilled) {
          const event = new CustomEvent('enter-pressed', {
            bubbles: true,
            composed: true,
          });
          this.dispatchEvent(event);
        }
      },

      handlePaste: e => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');

        const numbers = pastedText.replace(/[^0-9]/g, '').slice(0, 6);

        if (numbers.length > 0) {
          this.__verificationCode = numbers
            .split('')
            .concat(Array(6 - numbers.length).fill(''));

          this.requestUpdate();

          this.onChange(numbers, numbers.length === 6);

          if (numbers.length === 6 && this.autoSubmitOnComplete) {
            this.onChange(numbers, true);
          }

          if (numbers.length < 6) {
            const nextInput =
              this.shadowRoot.querySelectorAll('.code-input')[numbers.length];
            if (nextInput) nextInput.focus();
          }
        }
      },

      resend: () => {
        this.__verificationCode = ['', '', '', '', '', ''];

        this.__error = false;

        this.__codeResent = true;

        this.onResend();

        this.requestUpdate();
      },
    };
  }

  static get styles() {
    return [
      css`
        .code-verification-container {
          margin-top: 20px;
          max-width: 300px;
        }

        .code-inputs {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
        }

        .code-input {
          width: 40px;
          height: 40px;
          font-size: 20px;
          text-align: center;
          border: 2px solid ${CSS_COLOR_GREY_1};
          border-radius: 4px;
          margin-right: 5px;
        }

        .code-input:focus {
          border-color: ${CSS_COLOR_HIGHLIGHT};
          outline: none;
        }

        .error-message {
          color: var(--neb-color-error, #ff0000);
          margin-top: 8px;
          font-size: 14px;
        }

        .resend-link {
          cursor: pointer;
          text-decoration: underline;
        }

        .code-input.error {
          border-color: var(--neb-color-error, #ff0000);
        }

        .resent-message {
          margin-top: 8px;
          font-size: 14px;
          color: #555;
        }
      `,
    ];
  }

  clearCode() {
    this.__verificationCode = ['', '', '', '', '', ''];
    this.__error = false;
    this.onChange(this.__verificationCode.join(''), false);
    this.requestUpdate();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('__error') && this.__error === true) {
      setTimeout(() => {
        const firstInput = this.shadowRoot?.querySelector('.code-input');

        if (firstInput) {
          firstInput.focus();
          firstInput.select();
        }
      }, 0);
    }
  }

  render() {
    return html`
      <div class="code-verification-container">
        <div id="${ELEMENTS.verificationCodeInputs.id}" class="code-inputs">
          ${this.__verificationCode.map(
            (digit, index) => html`
              <input
                class="code-input ${this.__error ? 'error' : ''}"
                pattern="[0-9]*"
                inputmode="numeric"
                maxlength="1"
                .value="${digit}"
                autocomplete="one-time-code"
                tabindex="0"
                @focus="${e => {
                  e.target.select();
                }}"
                @click="${e => {
                  e.target.select();
                }}"
                @input="${e =>
                  this.__handlers.verificationCodeChange(index, e)}"
                @keydown="${e => this.__handlers.handleKeyDown(index, e)}"
                @paste="${index === 0 ? this.__handlers.handlePaste : null}"
              />
            `,
          )}
        </div>
        ${this.__error && !this.authenticator
          ? html`
              <div class="error-message">
                Invalid code.
                <span class="resend-link" @click="${this.__handlers.resend}"
                  >Click here to resend</span
                >
              </div>
            `
          : ''}
        ${this.__codeResent && !this.__error
          ? html` <div class="resent-message">Code Resent!</div> `
          : ''}
      </div>
    `;
  }
}

customElements.define('neb-two-factor-auth', NebTwoFactorAuth);
